import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as TestIcon,
  Science as ExperimentIcon,
  Message as MessageIcon,
  SmartToy as BotIcon,
  CompareArrows as CompareIcon,
  Visibility as PreviewIcon,
  Rocket as DeployIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  agentConfigService, 
  AgentConfiguration, 
  ConfigurationTestResponse,
  ConfigurationPreviewResponse,
  ABTestResponse,
  ConfigurationDeploymentResponse
} from '../../services/agentConfigService';

const ConfigurationTester: React.FC = () => {
  const [configurations, setConfigurations] = useState<AgentConfiguration[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | ''>('');
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState<ConfigurationTestResponse | null>(null);
  const [previewResult, setPreviewResult] = useState<ConfigurationPreviewResponse | null>(null);
  const [abTestResult, setABTestResult] = useState<ABTestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'single' | 'preview' | 'abtest' | 'deploy'>('single');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Preview testing state
  const [previewMessages, setPreviewMessages] = useState<string[]>(['']);
  
  // A/B testing state
  const [configAId, setConfigAId] = useState<number | ''>('');
  const [configBId, setConfigBId] = useState<number | ''>('');
  const [abTestMessages, setABTestMessages] = useState<string[]>(['']);
  
  // Deployment state
  const [deployConfigId, setDeployConfigId] = useState<number | ''>('');
  const [deploymentNotes, setDeploymentNotes] = useState('');
  const [deploymentResult, setDeploymentResult] = useState<ConfigurationDeploymentResponse | null>(null);

  // Predefined test messages for quick testing
  const predefinedMessages = [
    'Hola, estoy interesado en comprar un apartamento',
    '¿Qué proyectos tienen disponibles en Medellín?',
    '¿Cuál es el precio de los apartamentos en Bogotá?',
    'Me gustaría agendar una cita para ver un proyecto',
    '¿Tienen financiación disponible?',
    'Necesito información sobre el proyecto Acuarela del Parque'
  ];

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      const data = await agentConfigService.getConfigurations();
      setConfigurations(data);
    } catch (error) {
      showSnackbar('Error al cargar configuraciones', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTest = async () => {
    if (!testMessage.trim()) {
      showSnackbar('Por favor ingresa un mensaje para probar', 'error');
      return;
    }

    try {
      setLoading(true);
      const testRequest = {
        test_message: testMessage,
        config_id: selectedConfigId || undefined
      };

      const result = await agentConfigService.testConfiguration(testRequest);
      setTestResult(result);
      showSnackbar('Prueba ejecutada exitosamente', 'success');
    } catch (error) {
      showSnackbar('Error al ejecutar la prueba', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePredefinedMessage = (message: string) => {
    setTestMessage(message);
  };

  const handlePreview = async () => {
    const validMessages = previewMessages.filter(msg => msg.trim());
    if (validMessages.length === 0) {
      showSnackbar('Por favor ingresa al menos un mensaje para previsualizar', 'error');
      return;
    }

    try {
      setLoading(true);
      const previewRequest = {
        test_messages: validMessages,
        config_id: selectedConfigId || undefined
      };

      const result = await agentConfigService.previewConfiguration(previewRequest);
      setPreviewResult(result);
      showSnackbar('Previsualización ejecutada exitosamente', 'success');
    } catch (error) {
      showSnackbar('Error al ejecutar la previsualización', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleABTest = async () => {
    const validMessages = abTestMessages.filter(msg => msg.trim());
    if (validMessages.length === 0) {
      showSnackbar('Por favor ingresa al menos un mensaje para la prueba A/B', 'error');
      return;
    }

    if (!configAId || !configBId) {
      showSnackbar('Por favor selecciona ambas configuraciones para la prueba A/B', 'error');
      return;
    }

    if (configAId === configBId) {
      showSnackbar('Las configuraciones A y B deben ser diferentes', 'error');
      return;
    }

    try {
      setLoading(true);
      const abTestRequest = {
        config_a_id: configAId as number,
        config_b_id: configBId as number,
        test_messages: validMessages
      };

      const result = await agentConfigService.abTestConfigurations(abTestRequest);
      setABTestResult(result);
      showSnackbar('Prueba A/B ejecutada exitosamente', 'success');
    } catch (error) {
      showSnackbar('Error al ejecutar la prueba A/B', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async () => {
    if (!deployConfigId) {
      showSnackbar('Por favor selecciona una configuración para desplegar', 'error');
      return;
    }

    try {
      setLoading(true);
      const deployRequest = {
        config_id: deployConfigId as number,
        deployment_notes: deploymentNotes.trim() || undefined
      };

      const result = await agentConfigService.deployConfiguration(deployRequest);
      setDeploymentResult(result);
      showSnackbar('Configuración desplegada exitosamente', 'success');
      
      // Reload configurations to reflect changes
      await loadConfigurations();
    } catch (error) {
      showSnackbar('Error al desplegar la configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addPreviewMessage = () => {
    setPreviewMessages([...previewMessages, '']);
  };

  const removePreviewMessage = (index: number) => {
    if (previewMessages.length > 1) {
      setPreviewMessages(previewMessages.filter((_, i) => i !== index));
    }
  };

  const updatePreviewMessage = (index: number, value: string) => {
    const updated = [...previewMessages];
    updated[index] = value;
    setPreviewMessages(updated);
  };

  const addABTestMessage = () => {
    setABTestMessages([...abTestMessages, '']);
  };

  const removeABTestMessage = (index: number) => {
    if (abTestMessages.length > 1) {
      setABTestMessages(abTestMessages.filter((_, i) => i !== index));
    }
  };

  const updateABTestMessage = (index: number, value: string) => {
    const updated = [...abTestMessages];
    updated[index] = value;
    setABTestMessages(updated);
  };

  const getConfigTypeLabel = (type: string) => {
    switch (type) {
      case 'tone': return 'Tono';
      case 'behavior': return 'Comportamiento';
      case 'response_template': return 'Plantilla de Respuesta';
      default: return type;
    }
  };

  const getConfigTypeColor = (type: string) => {
    switch (type) {
      case 'tone': return 'primary';
      case 'behavior': return 'secondary';
      case 'response_template': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ExperimentIcon />
        Probar Configuración del Agente
      </Typography>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { key: 'single', label: 'Prueba Simple', icon: <TestIcon /> },
            { key: 'preview', label: 'Previsualización', icon: <PreviewIcon /> },
            { key: 'abtest', label: 'Prueba A/B', icon: <CompareIcon /> },
            { key: 'deploy', label: 'Despliegue', icon: <DeployIcon /> }
          ].map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'contained' : 'outlined'}
              startIcon={tab.icon}
              onClick={() => setActiveTab(tab.key as any)}
              size="small"
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Single Test Tab */}
      {activeTab === 'single' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuración de Prueba
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Configuración a Probar</InputLabel>
                <Select
                  value={selectedConfigId}
                  label="Configuración a Probar"
                  onChange={(e) => setSelectedConfigId(e.target.value as number | '')}
                >
                  <MenuItem value="">
                    <em>Usar configuración activa</em>
                  </MenuItem>
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.config_name} ({getConfigTypeLabel(config.config_type)})
                      {config.is_active && ' - Activa'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Mensaje de Prueba"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                multiline
                rows={4}
                fullWidth
                placeholder="Escribe el mensaje que quieres probar con la configuración..."
              />

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <TestIcon />}
                onClick={handleTest}
                disabled={loading || !testMessage.trim()}
                fullWidth
              >
                {loading ? 'Probando...' : 'Probar Configuración'}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Mensajes Predefinidos
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {predefinedMessages.map((message, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  onClick={() => handlePredefinedMessage(message)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {message}
                </Button>
              ))}
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resultado de la Prueba
            </Typography>

            {testResult ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Configuración Utilizada
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {testResult.config_used.config_name}
                      </Typography>
                      <Chip
                        label={getConfigTypeLabel(testResult.config_used.config_type)}
                        color={getConfigTypeColor(testResult.config_used.config_type) as any}
                        size="small"
                      />
                      {testResult.config_used.is_active && (
                        <Chip label="Activa" color="success" size="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Probado el: {new Date(testResult.timestamp).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MessageIcon fontSize="small" />
                      Mensaje del Usuario
                    </Typography>
                    <Typography variant="body2">
                      {testResult.test_message}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ backgroundColor: 'action.hover' }}>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BotIcon fontSize="small" />
                      Respuesta Generada
                    </Typography>
                    <Typography variant="body2">
                      {testResult.generated_response}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ExperimentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Ejecuta una prueba para ver los resultados aquí
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Previsualización de Configuración
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Configuración a Previsualizar</InputLabel>
                <Select
                  value={selectedConfigId}
                  label="Configuración a Previsualizar"
                  onChange={(e) => setSelectedConfigId(e.target.value as number | '')}
                >
                  <MenuItem value="">
                    <em>Usar configuración activa</em>
                  </MenuItem>
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.config_name} ({getConfigTypeLabel(config.config_type)})
                      {config.is_active && ' - Activa'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">
                Mensajes de Prueba
              </Typography>

              {previewMessages.map((message, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label={`Mensaje ${index + 1}`}
                    value={message}
                    onChange={(e) => updatePreviewMessage(index, e.target.value)}
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => removePreviewMessage(index)}
                    disabled={previewMessages.length === 1}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addPreviewMessage}
                disabled={previewMessages.length >= 10}
                size="small"
              >
                Agregar Mensaje
              </Button>

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <PreviewIcon />}
                onClick={handlePreview}
                disabled={loading || previewMessages.every(msg => !msg.trim())}
                fullWidth
              >
                {loading ? 'Previsualizando...' : 'Previsualizar Configuración'}
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resultados de Previsualización
            </Typography>

            {previewResult ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Configuración Utilizada
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {previewResult.config_used.config_name}
                      </Typography>
                      <Chip
                        label={getConfigTypeLabel(previewResult.config_used.config_type)}
                        color={getConfigTypeColor(previewResult.config_used.config_type) as any}
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Previsualizado el: {new Date(previewResult.timestamp).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>

                {previewResult.responses.map((response, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Conversación {index + 1}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Usuario:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {response.test_message}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Bot:
                        </Typography>
                        <Typography variant="body2" sx={{ backgroundColor: 'action.hover', p: 1, borderRadius: 1 }}>
                          {response.generated_response}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PreviewIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Ejecuta una previsualización para ver los resultados aquí
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* A/B Test Tab */}
      {activeTab === 'abtest' && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuración de Prueba A/B
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Configuración A</InputLabel>
                <Select
                  value={configAId}
                  label="Configuración A"
                  onChange={(e) => setConfigAId(e.target.value as number | '')}
                >
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.config_name} ({getConfigTypeLabel(config.config_type)})
                      {config.is_active && ' - Activa'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Configuración B</InputLabel>
                <Select
                  value={configBId}
                  label="Configuración B"
                  onChange={(e) => setConfigBId(e.target.value as number | '')}
                >
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.config_name} ({getConfigTypeLabel(config.config_type)})
                      {config.is_active && ' - Activa'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Mensajes de Prueba
            </Typography>

            {abTestMessages.map((message, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                <TextField
                  label={`Mensaje ${index + 1}`}
                  value={message}
                  onChange={(e) => updateABTestMessage(index, e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => removeABTestMessage(index)}
                  disabled={abTestMessages.length === 1}
                  sx={{ minWidth: 'auto', p: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addABTestMessage}
                disabled={abTestMessages.length >= 10}
                size="small"
              >
                Agregar Mensaje
              </Button>

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <CompareIcon />}
                onClick={handleABTest}
                disabled={loading || !configAId || !configBId || abTestMessages.every(msg => !msg.trim())}
              >
                {loading ? 'Ejecutando Prueba A/B...' : 'Ejecutar Prueba A/B'}
              </Button>
            </Box>
          </Paper>

          {abTestResult && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="A" color="primary" size="small" />
                  {abTestResult.config_a.config_name}
                </Typography>

                {abTestResult.responses_a.map((response, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="caption" color="text.secondary">
                        Mensaje {index + 1}:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {response.test_message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Respuesta:
                      </Typography>
                      <Typography variant="body2" sx={{ backgroundColor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1 }}>
                        {response.generated_response}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="B" color="secondary" size="small" />
                  {abTestResult.config_b.config_name}
                </Typography>

                {abTestResult.responses_b.map((response, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="caption" color="text.secondary">
                        Mensaje {index + 1}:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {response.test_message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Respuesta:
                      </Typography>
                      <Typography variant="body2" sx={{ backgroundColor: 'secondary.light', color: 'secondary.contrastText', p: 1, borderRadius: 1 }}>
                        {response.generated_response}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Deploy Tab */}
      {activeTab === 'deploy' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Desplegar Configuración
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Configuración a Desplegar</InputLabel>
                <Select
                  value={deployConfigId}
                  label="Configuración a Desplegar"
                  onChange={(e) => setDeployConfigId(e.target.value as number | '')}
                >
                  {configurations.filter(config => !config.is_active).map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.config_name} ({getConfigTypeLabel(config.config_type)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Notas de Despliegue (Opcional)"
                value={deploymentNotes}
                onChange={(e) => setDeploymentNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Describe los cambios o razones para este despliegue..."
              />

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <DeployIcon />}
                onClick={handleDeploy}
                disabled={loading || !deployConfigId}
                fullWidth
                color="warning"
              >
                {loading ? 'Desplegando...' : 'Desplegar a Producción'}
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resultado del Despliegue
            </Typography>

            {deploymentResult ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="success">
                  Configuración desplegada exitosamente
                </Alert>

                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Configuración Desplegada
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {deploymentResult.config_deployed.config_name}
                      </Typography>
                      <Chip label="ACTIVA" color="success" size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Desplegado el: {new Date(deploymentResult.deployed_at).toLocaleString()}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Por: {deploymentResult.deployed_by}
                    </Typography>
                  </CardContent>
                </Card>

                {deploymentResult.previous_active_config && (
                  <Card variant="outlined">
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Configuración Anterior
                      </Typography>
                      <Typography variant="body2">
                        {deploymentResult.previous_active_config.config_name}
                      </Typography>
                      <Chip label="DESACTIVADA" color="default" size="small" />
                    </CardContent>
                  </Card>
                )}

                {deploymentResult.deployment_notes && (
                  <Card variant="outlined">
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Notas del Despliegue
                      </Typography>
                      <Typography variant="body2">
                        {deploymentResult.deployment_notes}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DeployIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Selecciona una configuración para desplegar
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigurationTester;